import React from 'react';
import type { CSSProperties, HTMLAttributes } from 'react';

import { GRAY_25_HEX, GRAY_50_HEX } from 'shared';

import { DS_CLASS_NAME } from './constants';

const SHIMMER_KEYFRAMES =
  '@keyframes skeleton-shimmer{0%{background-position:-500px 0}100%{background-position:500px 0}}';

let shimmerStylesInjected = false;

function ensureShimmerKeyframes(): void {
  if (shimmerStylesInjected) return;
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-skeleton-shimmer', '');
  style.textContent = SHIMMER_KEYFRAMES;
  document.head.appendChild(style);
  shimmerStylesInjected = true;
}

type SkeletonVariant = 'text' | 'rect' | 'circle';
type SkeletonAnimation = 'shimmer' | 'pulse' | 'none';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
  animation?: SkeletonAnimation;
}

const RECT_BORDER_RADIUS_THRESHOLD = 48;

const getRectBorderRadius = (width: number | string, height: number | string): number => {
  const w = typeof width === 'number' ? width : null;
  const h = typeof height === 'number' ? height : null;
  if (w !== null && h !== null) {
    const minDim = Math.min(w, h);
    return minDim >= RECT_BORDER_RADIUS_THRESHOLD ? 16 : 8;
  }
  return 8;
};

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'rect',
  width = '100%',
  height = 16,
  borderRadius,
  animation = 'shimmer',
  style,
  className,
  ...rest
}) => {
  const resolvedBorderRadius: CSSProperties['borderRadius'] =
    borderRadius ?? (variant === 'circle' ? '999px' : variant === 'text' ? 4 : getRectBorderRadius(width, height));

  const baseColor = GRAY_50_HEX;
  const highlightColor = GRAY_25_HEX;

  const isShimmer = animation === 'shimmer';
  if (isShimmer) {
    ensureShimmerKeyframes();
  }

  const animationStyles: CSSProperties = isShimmer
    ? {
        backgroundImage: `linear-gradient(
            90deg,
            ${baseColor} 0%,
            ${highlightColor} 20%,
            ${baseColor} 40%,
            ${baseColor} 100%
          )`,
        backgroundSize: '1000px 100%',
        backgroundPosition: '-500px 0',
        animation: 'skeleton-shimmer 1.5s linear infinite'
      }
    : animation === 'pulse'
      ? {
          animation: 'skeleton-pulse 1.4s ease-in-out infinite',
          backgroundColor: baseColor
        }
      : { backgroundColor: baseColor };

  const sizeStyles: CSSProperties = {
    width,
    height,
    borderRadius: resolvedBorderRadius
  };

  return (
    <div
      aria-hidden="true"
      className={[`${DS_CLASS_NAME}-skeleton`, className].filter(Boolean).join(' ')}
      style={{ ...sizeStyles, ...animationStyles, ...style }}
      {...rest}
    />
  );
};

export default Skeleton;
